import { Filter, Search, X } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { cn } from '../../lib/utils';

interface QuotesFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  statusFilter: string;
  onStatusChange: (value: string) => void;
  typeFilter: string;
  onTypeChange: (value: string) => void;
  onReset: () => void;
  hasActiveFilters: boolean;
  filteredCount: number;
  totalCount: number;
}

export function QuotesFilters({
  search,
  onSearchChange,
  statusFilter,
  onStatusChange,
  typeFilter,
  onTypeChange,
  onReset,
  hasActiveFilters,
  filteredCount,
  totalCount,
}: QuotesFiltersProps) {
  return (
    <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-3">
      <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center w-full">
        <div className="flex items-center gap-2 text-slate-400 shrink-0 md:border-r border-slate-200 md:pr-3">
          <Filter className="w-4 h-4" />
          <span className="text-sm font-semibold text-slate-700">Filtres</span>
        </div>

        <label className="flex-1 min-w-0 flex items-center gap-2 px-3 bg-slate-50 border border-slate-200 rounded-md cursor-text focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary/30 transition-all">
          <Search className="w-4 h-4 text-slate-400 shrink-0" />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="flex-grow min-w-0 py-1.5 bg-transparent border-none outline-none text-sm text-slate-900 placeholder:text-slate-400"
            placeholder="Référence, espace ou client..."
          />
        </label>

        <Select value={statusFilter} onValueChange={onStatusChange}>
          <SelectTrigger
            className={cn(
              'w-auto min-w-[130px] shrink-0 bg-white h-auto py-1.5 focus:ring-primary/20',
              statusFilter !== 'all' ? 'border-primary bg-primary/5 text-primary font-medium' : 'border-slate-200 text-slate-600 hover:bg-slate-50',
            )}
          >
            <SelectValue placeholder="Tous statuts" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            <SelectItem value="DRAFT">Brouillon</SelectItem>
            <SelectItem value="SENT">Envoyé</SelectItem>
            <SelectItem value="ACCEPTED">Accepté</SelectItem>
            <SelectItem value="REJECTED">Refusé</SelectItem>
            <SelectItem value="EXPIRED">Expiré</SelectItem>
          </SelectContent>
        </Select>

        <Select value={typeFilter} onValueChange={onTypeChange}>
          <SelectTrigger
            className={cn(
              'w-auto min-w-[130px] shrink-0 bg-white h-auto py-1.5 focus:ring-primary/20',
              typeFilter !== 'all' ? 'border-primary bg-primary/5 text-primary font-medium' : 'border-slate-200 text-slate-600 hover:bg-slate-50',
            )}
          >
            <SelectValue placeholder="Tous types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous types</SelectItem>
            <SelectItem value="Standard">Standard</SelectItem>
            <SelectItem value="Custom">Personnalisé</SelectItem>
          </SelectContent>
        </Select>

        {hasActiveFilters && (
          <button onClick={onReset} className="flex items-center justify-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 px-2 py-1.5 shrink-0 transition-colors">
            <X className="w-4 h-4" /> Réinitialiser
          </button>
        )}

        <span className="shrink-0 whitespace-nowrap text-sm text-slate-500 font-medium md:ml-auto md:pl-3 md:border-l border-slate-200 pt-2 md:pt-0 border-t md:border-t-0 text-center md:text-left">
          {filteredCount === totalCount ? `${totalCount} résultats` : `${filteredCount} sur ${totalCount}`}
        </span>
      </div>
    </div>
  );
}
